// Feedback submissions — templateID 12 (Feedback Form) on the old ERP
// backend. Read-only historical data (confirmed live 2026-08-20: 15 real
// records) — like Contact Us (templateID 8), this is a public-form submission
// table with no status/threading fields and no write path used by this app.
// Field-slot layout confirmed directly from a live response's field labels:
// Id (write _1, read index 3), Name (_2/4), Mobile (_3/5), Email (_4/6),
// Course (_5/7), ExperienceRate (_6/8), FeedBack (_7/9).
import { addUpdateTemplateDataWeb, cell, getTemplateDataList, isApiConnected, type TemplateRow } from "@/lib/apiClient";

const FEEDBACK_TEMPLATE_ID = 12;

const SCHEMA = {
  id: 0,
  createDt: 1,
  refId: 3,
  name: 4,
  mobile: 5,
  email: 6,
  course: 7,
  experienceRate: 8,
  feedback: 9,
} as const;

export interface FeedbackRow {
  id: string;
  name: string;
  mobile: string;
  email: string;
  course: string;
  experienceRate: number;
  feedback: string;
  date: string;
}

function transformRow(row: TemplateRow[]): FeedbackRow {
  return {
    id: cell(row, SCHEMA.id),
    name: cell(row, SCHEMA.name),
    mobile: cell(row, SCHEMA.mobile),
    email: cell(row, SCHEMA.email),
    course: cell(row, SCHEMA.course),
    experienceRate: Number(cell(row, SCHEMA.experienceRate) || 0),
    feedback: cell(row, SCHEMA.feedback),
    date: cell(row, SCHEMA.createDt),
  };
}

export async function listFeedback(): Promise<FeedbackRow[]> {
  if (!isApiConnected()) return [];
  const { response } = await getTemplateDataList(FEEDBACK_TEMPLATE_ID, {}, { pgSize: 500 });
  return (response.records || []).map(transformRow);
}

function generateFeedbackId(): string {
  return `FDB-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
}

// Public feedback-form write path — writes to the same templateID 12 the
// admin feedback panel already reads. Field order matches SCHEMA above:
// _1 refId, _2 name, _3 mobile, _4 email, _5 course, _6 experienceRate,
// _7 feedback. _1 must be non-empty — the backend rejects the write with
// "Failed! Template Data Mandatory Fields Id does not exists." otherwise
// (confirmed live), so a client-generated reference id is sent instead of
// leaving it blank.
export async function submitFeedback(params: {
  name: string;
  mobile: string;
  email: string;
  course: string;
  experienceRate: number;
  feedback: string;
}): Promise<FeedbackRow> {
  const resp = await addUpdateTemplateDataWeb(FEEDBACK_TEMPLATE_ID, {
    _1: generateFeedbackId(),
    _2: params.name,
    _3: params.mobile,
    _4: params.email,
    _5: params.course,
    _6: String(params.experienceRate),
    _7: params.feedback,
  });
  if (resp.hasErrors) throw new Error(resp.errors || "Failed to submit feedback");

  // Read the row back and confirm it actually persisted before telling the
  // user their feedback was submitted — some templateIDs on this backend
  // report success while silently dropping every field (see
  // docs/BACKEND_SPEC_MISSING_ENDPOINTS.md).
  const rows = await listFeedback();
  const saved = rows.find(
    (f) => f.name === params.name && f.email === params.email && f.feedback === params.feedback
  );
  if (!saved) {
    throw new Error("Your feedback couldn't be saved right now. Please try again in a moment.");
  }
  return saved;
}
